export type CrosshairStyle = 'dot' | 'cross' | 'circle';
export type CrosshairColor = 'white' | 'cyan' | 'green' | 'red';

export type FpsCrosshairSettings = {
  style: CrosshairStyle;
  color: CrosshairColor;
  /** Line length and dot size multiplier (0.8–1.4). */
  size: number;
};

const LS_KEY = 'stormsurge-crosshair-v1';

export const DEFAULT_CROSSHAIR_SETTINGS: FpsCrosshairSettings = {
  style: 'cross',
  color: 'white',
  size: 1,
};

const STYLES: CrosshairStyle[] = ['dot', 'cross', 'circle'];
const COLORS: CrosshairColor[] = ['white', 'cyan', 'green', 'red'];

export function crosshairColorCss(color: CrosshairColor): string {
  switch (color) {
    case 'cyan':
      return 'rgba(46,230,255,0.95)';
    case 'green':
      return 'rgba(74,222,128,0.95)';
    case 'red':
      return 'rgba(248,113,113,0.95)';
    default:
      return 'rgba(255,255,255,0.95)';
  }
}

export function crosshairGlowCss(color: CrosshairColor): string {
  switch (color) {
    case 'cyan':
      return 'rgba(46,230,255,0.45)';
    case 'green':
      return 'rgba(74,222,128,0.45)';
    case 'red':
      return 'rgba(248,113,113,0.45)';
    default:
      return 'rgba(255,255,255,0.45)';
  }
}

function clampSize(n: number): number {
  return Math.max(0.8, Math.min(1.4, n));
}

function normalizeStyle(raw: string | undefined): CrosshairStyle | undefined {
  if (raw === 'tactical') return 'circle';
  if (raw && STYLES.includes(raw as CrosshairStyle)) return raw as CrosshairStyle;
  return undefined;
}

/** Read persisted crosshair settings; missing keys use defaults. */
export function loadCrosshairSettings(): FpsCrosshairSettings {
  const base = { ...DEFAULT_CROSSHAIR_SETTINGS };
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<FpsCrosshairSettings> & { style?: string };
    const style = normalizeStyle(parsed.style);
    if (style) base.style = style;
    if (parsed.color && COLORS.includes(parsed.color)) base.color = parsed.color;
    if (typeof parsed.size === 'number' && !Number.isNaN(parsed.size)) {
      base.size = clampSize(parsed.size);
    }
  } catch {
    /* ignore corrupt storage */
  }
  return base;
}

/** Persist crosshair settings to localStorage. */
export function saveCrosshairSettings(settings: FpsCrosshairSettings): void {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        style: settings.style,
        color: settings.color,
        size: clampSize(settings.size),
      }),
    );
  } catch {
    /* ignore quota / private mode */
  }
}
